import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '../../../../../../../components/ui/card';
import { Badge } from '../../../../../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../../../../components/ui/tabs';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface PassParametersTableProps {
    jobData: any;
}

interface PassData {
    Type: string;
    Torch: string;
    Pass: string;
    PassName: string;
    PassPendName: string;
    PassGroupNumber: string;
    EndingAngle: string;
    PassEnable: string;
    CommonWeld?: any;
    MotorJog?: any;
    WireFeed?: any;
    Travel?: any;
    Oscillation?: any;
    PowerSupplyController?: any;
    PowerSupplyPulseArc?: any;
    PowerSupplyShortArc?: any;
    ArcStartup?: any;
    ArcTermination?: any;
    VerticalTracking?: any;
    CrossTracking?: any;
}

const PassParametersTable: React.FC<PassParametersTableProps> = ({ jobData }) => {
    const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
    const [activeTab, setActiveTab] = useState<'all' | 'lead' | 'trail'>('all');

    // Filter pass data (exclude JOB type)
    const passData = useMemo(() => {
        if (!jobData?.parsed_json_data) return [];
        return jobData.parsed_json_data.filter((item: any) => item.Type === 'PASS') as PassData[];
    }, [jobData]);

    // Group passes by pass number
    const groupedPasses = useMemo(() => {
        const groups: { [key: string]: { lead?: PassData; trail?: PassData } } = {};
        
        passData.forEach((pass) => {
            const passNumber = pass.Pass;
            if (!groups[passNumber]) {
                groups[passNumber] = {};
            }
            
            if (pass.Torch === 'LEAD') {
                groups[passNumber].lead = pass;
            } else if (pass.Torch === 'TRAIL') {
                groups[passNumber].trail = pass;
            }
        });
        
        return groups;
    }, [passData]);

    // Filter passes based on active tab
    const filteredPasses = useMemo(() => {
        const passes = Object.entries(groupedPasses);
        
        switch (activeTab) {
            case 'lead':
                return passes.filter(([_, data]) => data.lead);
            case 'trail':
                return passes.filter(([_, data]) => data.trail);
            default:
                return passes;
        }
    }, [groupedPasses, activeTab]);

    const toggleSection = (section: string) => {
        const newExpanded = new Set(expandedSections);
        if (newExpanded.has(section)) {
            newExpanded.delete(section);
        } else {
            newExpanded.add(section);
        }
        setExpandedSections(newExpanded);
    };

    const renderParameterSection = (title: string, data: any, passKey: string) => {
        if (!data) return null;
        
        const sectionKey = `${passKey}-${title}`;
        const isExpanded = expandedSections.has(sectionKey);
        
        return (
            <div key={title} className="border-b border-gray-200 last:border-b-0">
                <button
                    onClick={() => toggleSection(sectionKey)}
                    className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-50 transition-colors"
                >
                    <span className="font-medium text-gray-900">{title}</span>
                    {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                    ) : (
                        <ChevronRight className="h-4 w-4 text-gray-500" />
                    )}
                </button>
                {isExpanded && (
                    <div className="px-3 pb-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {Object.entries(data).map(([key, value]) => (
                                <div key={key} className="space-y-1">
                                    <label className="text-xs font-medium text-gray-600">
                                        {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                    </label>
                                    <div className="text-sm text-gray-900 font-mono bg-gray-50 px-2 py-1 rounded text-xs">
                                        {String(value)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderPassCard = (passNumber: string, data: { lead?: PassData; trail?: PassData }) => {
        const sections = [
            'CommonWeld', 'MotorJog', 'WireFeed', 'Travel', 'Oscillation',
            'PowerSupplyController', 'PowerSupplyPulseArc', 'PowerSupplyShortArc',
            'ArcStartup', 'ArcTermination', 'VerticalTracking', 'CrossTracking'
        ];

        return (
            <Card key={passNumber} className="mb-4">
                <div className="p-6 pb-3">
                    <h3 className="text-base font-semibold flex items-center justify-between">
                        <span>{passNumber}</span>
                        <div className="flex gap-2">
                            {data.lead && (
                                <Badge variant={data.lead.PassEnable === 'Yes' ? 'default' : 'secondary'}>
                                    Lead: {data.lead.PassPendName}
                                </Badge>
                            )}
                            {data.trail && (
                                <Badge variant={data.trail.PassEnable === 'Yes' ? 'default' : 'secondary'}>
                                    Trail: {data.trail.PassPendName}
                                </Badge>
                            )}
                        </div>
                    </h3>
                </div>
                <CardContent className="pt-0">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Lead Torch */}
                        {data.lead && (
                            <div className="space-y-2">
                                <h4 className="font-medium text-gray-900 border-b pb-1">
                                    Lead Torch - {data.lead.PassName}
                                </h4>
                                <div className="space-y-1 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Pass Enable:</span>
                                        <Badge variant={data.lead.PassEnable === 'Yes' ? 'default' : 'secondary'}>
                                            {data.lead.PassEnable}
                                        </Badge>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Group:</span>
                                        <span className="font-mono">{data.lead.PassGroupNumber}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Ending Angle:</span>
                                        <span className="font-mono">{data.lead.EndingAngle}°</span>
                                    </div>
                                </div>
                                
                                {/* Parameter sections for Lead */}
                                <div className="mt-3 space-y-1">
                                    {sections.map(section => 
                                        renderParameterSection(section, (data.lead as any)?.[section], `${passNumber}-lead`)
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Trail Torch */}
                        {data.trail && (
                            <div className="space-y-2">
                                <h4 className="font-medium text-gray-900 border-b pb-1">
                                    Trail Torch - {data.trail.PassName}
                                </h4>
                                <div className="space-y-1 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Pass Enable:</span>
                                        <Badge variant={data.trail.PassEnable === 'Yes' ? 'default' : 'secondary'}>
                                            {data.trail.PassEnable}
                                        </Badge>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Group:</span>
                                        <span className="font-mono">{data.trail.PassGroupNumber}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Ending Angle:</span>
                                        <span className="font-mono">{data.trail.EndingAngle}°</span>
                                    </div>
                                </div>
                                
                                {/* Parameter sections for Trail */}
                                <div className="mt-3 space-y-1">
                                    {sections.map(section => 
                                        renderParameterSection(section, (data.trail as any)?.[section], `${passNumber}-trail`)
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        );
    };

    if (!passData.length) {
        return (
            <div className="text-center py-12 text-gray-500">
                <p>No pass parameters found</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <Card>
                
                <CardContent>
                <div className="space-y-4">
                                {filteredPasses.map(([passNumber, data]) => 
                                    renderPassCard(passNumber, data)
                                )}
                            </div>
                            {/* </div>
                    <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'all' | 'lead' | 'trail')}>
                        <TabsList className="grid w-full grid-cols-3">
                            <TabsTrigger value="all">All Configuration</TabsTrigger>
                            <TabsTrigger value="lead">Lead Torch</TabsTrigger>
                            <TabsTrigger value="trail">Trail Torch</TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="all" className="mt-4">
                            <div className="space-y-4">
                                {filteredPasses.map(([passNumber, data]) => 
                                    renderPassCard(passNumber, data)
                                )}
                            </div>
                        </TabsContent>
                        
                        <TabsContent value="lead" className="mt-4">
                            <div className="space-y-4">
                                {filteredPasses.map(([passNumber, data]) => 
                                    data.lead ? renderPassCard(passNumber, { lead: data.lead }) : null
                                )}
                            </div>
                        </TabsContent>
                        
                        <TabsContent value="trail" className="mt-4">
                            <div className="space-y-4">
                                {filteredPasses.map(([passNumber, data]) => 
                                    data.trail ? renderPassCard(passNumber, { trail: data.trail }) : null
                                )}
                            </div>
                        </TabsContent>
                    </Tabs> */}
                </CardContent>
            </Card>
        </div>
    );
};

export default PassParametersTable;
